import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  Handshake,
  Home,
  Lock,
  Megaphone,
  MessageSquare,
  ScanFace,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Star,
  TriangleAlert,
  Trophy,
  User,
  UserPlus,
  UserRound,
  Zap,
  BadgeDollarSign,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import SidebarLinkGroup from './SidebarLinkGroup';
import Logo from '../../../../assets/cab.svg';
import { hasAnyPermission, isAdminOrAbove, hasStrictPermission, PERMISSION } from '../../../../utils/admin-permissions';
import { getGuardStats } from '../../../../api/admin/query-guard.api';
import { cx } from '../../ui/cx';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

interface NavKid {
  label: string;
  to: string;
}
interface NavEntry {
  key: string;
  label: string;
  icon: LucideIcon;
  show: boolean;
  to?: string; // mục đơn
  kids?: NavKid[]; // nhóm mở rộng
  badge?: number;
}
interface NavSection {
  heading?: string;
  entries: NavEntry[];
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const location = useLocation();
  const { pathname } = location;

  // Badge đỏ cảnh báo rà truy vấn: để admin thấy NGAY ở menu, không phải mở trang mới biết.
  // Lỗi/không có quyền -> im lặng về 0, không làm hỏng sidebar.
  const [guardWarn, setGuardWarn] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () =>
      getGuardStats()
        .then((s) => alive && setGuardWarn(s?.unreviewed ?? 0))
        .catch(() => undefined);
    load();
    const id = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Phân quyền (RBAC): ẩn menu theo quyền của admin đang đăng nhập (đọc từ token).
  const canModerate = hasAnyPermission(PERMISSION.ContentModerate);
  const canUser = hasAnyPermission(PERMISSION.UserManage);
  const canCreator = hasAnyPermission(PERMISSION.CreatorApprove);
  const canCosmetic = hasAnyPermission(PERMISSION.CosmeticGrant);
  const canDonateEvent = hasAnyPermission(PERMISSION.DonateManage);
  const canFinance = hasAnyPermission(
    PERMISSION.FinanceView,
    PERMISSION.DonateManage,
    PERMISSION.WithdrawApprove
  );
  const canRoleManage = hasAnyPermission(PERMISSION.RoleManage);
  const canSystem = isAdminOrAbove();
  // Nhật ký hành động và Lịch sử đồng ý: máy chủ chỉ cho Admin/SuperAdmin (+ tài khoản Chỉ xem đọc)
  // — RequireAdmin không kèm quyền ở AuditAdminController/ConsentAdminController.
  const canAuditLogs = isAdminOrAbove();
  // Khu vực kiếm tiền (Cab Ads / Affiliate): ẩn hẳn nếu không được cấp quyền (strict, không fail-open).
  const canAds = hasStrictPermission(PERMISSION.AdsManage);
  const canAffiliate = hasStrictPermission(PERMISSION.AffiliateManage);
  const canArena = hasAnyPermission(PERMISSION.ArenaManage);

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true'
  );

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector('body')?.classList.add('sidebar-expanded');
    } else {
      document.querySelector('body')?.classList.remove('sidebar-expanded');
    }
  }, [sidebarExpanded]);

  // Menu theo màn AdminSidebar: 3 nhóm có tiêu đề. Giữ nguyên mục, liên kết và điều kiện quyền cũ.
  const sections: NavSection[] = [
    {
      entries: [{ key: 'home', label: 'Trang chủ', icon: Home, show: true, to: '/admin/home' }],
    },
    {
      heading: 'Nội dung và người dùng',
      entries: [
        {
          key: 'posts',
          label: 'Quản lý bài viết',
          icon: ClipboardCheck,
          show: canModerate,
          kids: [
            { label: 'Danh sách bài viết', to: '/admin/post/total' },
            { label: 'Bài viết chờ duyệt', to: '/admin/post/pending' },
            { label: 'Duyệt Cab xưa', to: '/admin/post/archive' },
            { label: 'Bài viết bị report', to: '/admin/post/reported' },
            { label: 'Bài viết bị ẩn', to: '/admin/post/hidden' },
          ],
        },
        {
          key: 'comments',
          label: 'Quản lý bình luận',
          icon: MessageSquare,
          show: canModerate,
          kids: [
            { label: 'Bình luận bị ẩn', to: '/admin/comment/hidden' },
            { label: 'Bình luận bị report', to: '/admin/comment/reported' },
            { label: 'Ảnh ava/bìa bị report', to: '/admin/profile-image/reported' },
            { label: 'Clan bị report', to: '/admin/clan/reported' },
            { label: 'Tài khoản bị report', to: '/admin/account/reported' },
          ],
        },
        {
          key: 'users',
          label: 'Quản lý người dùng',
          icon: User,
          show: canUser,
          kids: [
            { label: 'Danh sách người dùng', to: '/admin/user/total' },
            { label: 'Người dùng bị cấm', to: '/admin/user/banned' },
          ],
        },
        { key: 'creator', label: 'Yêu cầu Creator', icon: UserRound, show: canCreator, to: '/admin/request-creator' },
        { key: 'ekyc', label: 'Lượt eKYC', icon: ScanFace, show: canUser, to: '/admin/ekyc-usage' },
        { key: 'cosmetic', label: 'Quản lý khung / cosmetic', icon: Star, show: canCosmetic, to: '/admin/cosmetic' },
      ],
    },
    {
      heading: 'Kiếm tiền và quảng cáo',
      entries: [
        { key: 'event', label: 'Sự kiện donate', icon: CircleDollarSign, show: canDonateEvent, to: '/admin/su-kien-donate' },
        { key: 'ads', label: 'Quảng cáo (Cab Ads)', icon: Megaphone, show: canAds, to: '/admin/ads' },
        { key: 'deals', label: 'Hợp tác creator', icon: Handshake, show: canAds, to: '/admin/brand-deals' },
        { key: 'publisher', label: 'Ads Network (Publisher)', icon: BadgeDollarSign, show: canAds, to: '/admin/publisher' },
        { key: 'aff', label: 'Cab Shop (Affiliate)', icon: ShoppingCart, show: canAffiliate, to: '/admin/affiliate' },
        {
          key: 'donate',
          label: 'Quản lý Donate',
          icon: CircleDollarSign,
          show: canFinance,
          kids: [
            { label: 'Yêu cầu mở donate', to: '/admin/donate/request' },
            { label: 'Yêu cầu quyết toán', to: '/admin/donate/withdraw' },
            { label: 'Báo cáo doanh thu', to: '/admin/donate/revenue' },
            { label: 'Sổ nạp số dư', to: '/admin/donate/topup' },
          ],
        },
      ],
    },
    {
      heading: 'Hệ thống',
      entries: [
        { key: 'consent', label: 'Lịch sử đồng ý điều khoản', icon: ShieldCheck, show: canAuditLogs, to: '/admin/consent' },
        { key: 'game', label: 'Cấu hình Ca đào / Blind Box', icon: Zap, show: canCosmetic, to: '/admin/gamification' },
        { key: 'arena', label: 'Arena', icon: Trophy, show: canArena, to: '/admin/arena' },
        { key: 'security', label: 'Bảo mật (Authenticator)', icon: Lock, show: true, to: '/admin/security' },
        { key: 'roles', label: 'Phân quyền', icon: UserPlus, show: canRoleManage, to: '/admin/roles' },
        { key: 'audit', label: 'Nhật ký hành động', icon: ClipboardList, show: canAuditLogs, to: '/admin/audit-log' },
        {
          key: 'query',
          label: 'Rà truy vấn',
          icon: TriangleAlert,
          show: canSystem,
          to: '/admin/query-guard',
          badge: guardWarn,
        },
        { key: 'settings', label: 'Cài đặt', icon: Settings, show: canSystem, to: '/admin/settings' },
      ],
    },
  ];

  const isUnder = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  const renderEntry = (e: NavEntry) => {
    const Icon = e.icon;
    if (e.kids) {
      const groupActive = e.kids.some((k) => isUnder(k.to));
      return (
        <SidebarLinkGroup key={e.key} hidden={!e.show} activeCondition={groupActive} title={e.label}>
          {(handleClick, open) => (
            <>
              <button
                type="button"
                aria-expanded={open}
                className={cx('adm-nav-item', groupActive && 'adm-nav-item--active')}
                onClick={handleClick}
              >
                {groupActive && <span className="adm-nav-bar" aria-hidden="true" />}
                <Icon size={20} strokeWidth={2} className="adm-nav-icon" aria-hidden="true" />
                <span className="adm-nav-label">{e.label}</span>
                {open ? (
                  <ChevronUp size={16} className="adm-nav-caret" aria-hidden="true" />
                ) : (
                  <ChevronDown size={16} className="adm-nav-caret" aria-hidden="true" />
                )}
              </button>
              {open && (
                <div className="adm-nav-kids">
                  {e.kids!.map((k) => (
                    <NavLink
                      key={k.to}
                      to={k.to}
                      className={({ isActive }) => cx('adm-nav-kid', isActive && 'adm-nav-kid--active')}
                    >
                      {k.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </>
          )}
        </SidebarLinkGroup>
      );
    }
    if (!e.show) return null;
    return (
      <li key={e.key}>
        <NavLink
          to={e.to!}
          className={({ isActive }) => cx('adm-nav-item', isActive && 'adm-nav-item--active')}
        >
          {({ isActive }) => (
            <>
              {isActive && <span className="adm-nav-bar" aria-hidden="true" />}
              <Icon size={20} strokeWidth={2} className="adm-nav-icon" aria-hidden="true" />
              <span className="adm-nav-label">{e.label}</span>
              {!!e.badge && e.badge > 0 && (
                <span className="adm-nav-badge">{e.badge > 99 ? '99+' : e.badge}</span>
              )}
            </>
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <aside
      ref={sidebar}
      className={`adm-sidebar fixed left-0 top-0 z-9999 duration-300 ease-linear lg:sticky lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="adm-sidebar__logo">
        <NavLink to="/admin">
          <img src={Logo} alt="cab.vn" />
        </NavLink>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          aria-label="Đóng menu"
          className="adm-icon-btn lg:hidden"
        >
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
      </div>

      <div className="adm-sidebar__scroll">
        <nav aria-label="Menu quản trị">
          <ul className="adm-nav">
            {sections.map((s) => {
              const visible = s.entries.filter((e) => e.show);
              if (visible.length === 0) return null;
              return (
                <React.Fragment key={s.heading || 'top'}>
                  {s.heading && (
                    <li className="adm-nav-heading" role="presentation">
                      {s.heading}
                    </li>
                  )}
                  {visible.map(renderEntry)}
                </React.Fragment>
              );
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
